import React from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, Toolbar, useMediaQuery, Button, Container, Grid, Card, CardContent, Chip, List, ListItem, ListItemIcon, ListItemText, Divider,  } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { motion } from "framer-motion";
import HomeHeader from "../../Components/HomeHeader";
import HomeFooter from "../../Components/HomeFooter";
import routes from "../../Config/routes";
import { 
  AccountBalance, 
  MonetizationOn, 
  People, 
  ShowChart, 
  AutoAwesome 
} from "@mui/icons-material";

const fadeIn = { 
  hidden: { opacity: 0, y: 20 }, 
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { 
      duration: 0.6, 
      ease: [0.16, 1, 0.3, 1] 
    }
  } 
};

const sectionVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.15,
      duration: 0.8,
      ease: [0.16, 1, 0.3, 1]
    }
  })
};

const sections = [
  {
    title: "About Blitz",
    content: `Blitz is a comprehensive AI-powered collaboration management platform designed to streamline operations in the creator economy. By offering the world's largest influencer marketplace, enabling brands and agencies to effortlessly launch unlimited campaigns, and providing automation for partnerships and financial management, Blitz redefines how the creator economy operates.`,
    icon: <AutoAwesome sx={{ fontSize: 40 }} />
  },
  {
    title: "Accounts Payable & Vendors",
    content: `BlitzPay enables companies to accelerate collaboration efforts with advanced payments to creators. Foster trust, shorten payment cycles, and keep creative workflows running smoothly.`,
    icon: <AccountBalance sx={{ fontSize: 40 }} />
  },
  {
    title: "Marketplace & Collaboration",
    content: [
      "AI-Powered Campaign Management: Leverage data-driven insights",
      "Data-Driven Reporting: Make informed decisions with detailed metrics",
      "Immense Personalization: Tailor partnerships to your unique needs",
      "Seamless Integration: Efficient workflows with existing tools",
      "Transparency & Compliance: Negotiate confidently with clear pricing",
      "Scalability & Growth: Scale effortlessly as your network expands"
    ],
    icon: <People sx={{ fontSize: 40 }} />
  },
  {
    title: "For Creators",
    content: [
      "AI Manager: Improve deal flow and discover opportunities",
      "Access Top Brands: Collaborate with leading companies",
      "Business Management Suite: Handle contracts, legal, and taxes",
      "Financial Services: Optimize finances and plan investments"
    ],
    icon: <MonetizationOn sx={{ fontSize: 40 }} />
  },
  {
    title: "Increase Revenue",
    content: `Creators report 2–3× revenue increases through Blitz's tools. Unlock sustainable income growth with streamlined deal flows, financial management, and premium partnerships.`,
    icon: <ShowChart sx={{ fontSize: 40 }} />
  }
];

export default function BlitzSummary() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  return (
    <Box sx={{ 
      backgroundColor: theme.palette.background.default,
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <HomeHeader />
      <Toolbar />

      {/* Hero Section */}
      <Box sx={{
        background: 'linear-gradient(135deg, #0f2027 0%, #203a43 50%, #2c5364 100%)',
        color: '#fff',
        py: 15,
        px: 2,
        textAlign: 'center'
      }}>
        <Container maxWidth="md">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeIn}
          >
            <Typography 
              variant={isMobile ? "h3" : "h2"} 
              sx={{ 
                fontWeight: 800, 
                mb: 3,
                letterSpacing: '-0.5px'
              }}
            >
              Revolutionize Creator Economy Operations
            </Typography>
            <Typography 
              variant="h6" 
              sx={{ 
                mb: 5, 
                maxWidth: 700, 
                mx: 'auto',
                opacity: 0.9,
                lineHeight: 1.7
              }}
            >
              AI-powered platform for seamless campaign management, payments, and financial operations
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate(routes.onboard)}
              sx={{
                px: 6,
                py: 2,
                fontSize: '1.1rem',
                fontWeight: 700,
                backgroundColor: theme.palette.primary.main,
                color: '#fff',
                borderRadius: 2,
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                '&:hover': { 
                  backgroundColor: theme.palette.primary.dark,
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 25px rgba(0, 0, 0, 0.2)'
                },
                transition: 'all 0.3s ease',
              }}
            >
              Get Started
            </Button>
          </motion.div>
        </Container>
      </Box>

      {/* Content Sections */}
      <Container maxWidth="lg" sx={{ py: 10, px: { xs: 2, md: 4 } }}>
        <Grid container spacing={8}>
          {sections.map((sec, idx) => (
            <Grid item xs={12} key={sec.title}>
              <motion.div
                custom={idx}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={sectionVariants}
              >
                <Card sx={{ 
                  borderRadius: 4,
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
                  border: '1px solid rgba(0, 0, 0, 0.05)',
                  overflow: 'hidden',
                  height: '100%'
                }}>
                  <CardContent sx={{ p: { xs: 3, md: 5 } }}>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      mb: 4
                    }}>
                      <Box sx={{
                        width: 70,
                        height: 70,
                        borderRadius: '50%',
                        backgroundColor: theme.palette.primary.light,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: 3,
                        flexShrink: 0
                      }}>
                        {sec.icon}
                      </Box>
                      <Typography 
                        variant="h4" 
                        sx={{ 
                          fontWeight: 700,
                          letterSpacing: '-0.5px'
                        }}
                      >
                        {sec.title}
                      </Typography>
                    </Box>
                    
                    {Array.isArray(sec.content) ? (
                      <List disablePadding>
                        {sec.content.map((item, i) => (
                          <React.Fragment key={i}>
                            <ListItem sx={{ px: 0, py: 1 }}>
                              <ListItemIcon sx={{ minWidth: 36 }}>
                                <Chip 
                                  label={i+1} 
                                  size="small"
                                  sx={{ 
                                    backgroundColor: theme.palette.primary.light, 
                                    color: theme.palette.primary.contrastText,
                                    fontWeight: 700
                                  }}
                                />
                              </ListItemIcon>
                              <ListItemText 
                                primary={item} 
                                primaryTypographyProps={{ 
                                  variant: 'body1',
                                  sx: { fontWeight: 500 } 
                                }}
                              />
                            </ListItem>
                            {i < sec.content.length - 1 && <Divider sx={{ my: 1 }} />}
                          </React.Fragment>
                        ))}
                      </List>
                    ) : (
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          lineHeight: 1.8, 
                          fontSize: '1.1rem',
                          color: theme.palette.text.secondary
                        }}
                      >
                        {sec.content}
                      </Typography>
                    )}
                    
                    {idx === sections.length - 1 && (
                      <Button
                        variant="contained"
                        onClick={() => navigate(routes.onboard)}
                        sx={{
                          mt: 4,
                          px: 5,
                          py: 1.5,
                          fontWeight: 700,
                          fontSize: '1rem',
                          borderRadius: 2,
                          boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)',
                          '&:hover': { 
                            transform: 'translateY(-2px)',
                            boxShadow: '0 6px 20px rgba(0, 0, 0, 0.15)'
                          },
                          transition: 'all 0.3s ease',
                        }}
                      >
                        Start Earning More
                      </Button>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* CTA Section */}
      <Box sx={{
        background: 'linear-gradient(135deg, #0f2027 0%, #203a43 100%)',
        color: '#fff',
        py: 10,
        textAlign: 'center'
      }}>
        <Container maxWidth="md">
          <Typography variant="h3" sx={{ fontWeight: 800, mb: 3 }}>
            Ready to Transform Your Creator Operations?
          </Typography>
          <Typography variant="h6" sx={{ mb: 5, maxWidth: 600, mx: 'auto', opacity: 0.9 }}>
            Join thousands of brands and creators already using Blitz to streamline their workflows and increase revenue
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate(routes.onboard)}
            sx={{
              px: 8,
              py: 2,
              fontSize: '1.1rem',
              fontWeight: 700,
              backgroundColor: theme.palette.primary.main,
              color: '#fff',
              borderRadius: 2,
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
              '&:hover': { 
                backgroundColor: theme.palette.primary.dark,
                transform: 'translateY(-3px)',
                boxShadow: '0 8px 25px rgba(0, 0, 0, 0.3)'
              },
              transition: 'all 0.3s ease',
            }}
          >
            Get Started Free
          </Button>
        </Container>
      </Box>

      <HomeFooter />
    </Box>
  );
}